import {
  Theme,
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmIconButton,
  WhmTypography,
} from "@securitize/reactjs-whm";
import CloseIcon from "@mui/icons-material/Close";
import taxFormSuccessImg from "../../../assets/tax-form-success.png";
import { TranslateHtml } from "../../../components/Translation/Translate";
import useTranslations from "../../../hooks/useTranslations";
import { translationKeys } from "../../../contexts/I18nContext";
import { getTaxFormLabel } from "../../../common/taxForms.utils";
import { TaxFormType } from "../../../types/documents.interface";

const STORAGE_KEY = "submittedTaxFormType";

/**
 * The form view reloads after submitting, so the Dashboard picks the modal up from here
 */
export const markTaxFormSubmitted = (formType: TaxFormType) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, formType);
  } catch (error) {
    console.warn(
      "[TaxFormSubmittedModal] sessionStorage.setItem failed:",
      error,
    );
  }
};

export const getSubmittedTaxFormType = (): TaxFormType | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) as TaxFormType | null;
  } catch (error) {
    console.warn(
      "[TaxFormSubmittedModal] sessionStorage.getItem failed:",
      error,
    );
    return null;
  }
};

const clearSubmittedTaxFormType = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn(
      "[TaxFormSubmittedModal] sessionStorage.removeItem failed:",
      error,
    );
  }
};

const formatModalDate = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

type Props = {
  formType: TaxFormType;
  expirationDate?: Date;
  onClose: () => void;
};

function TaxFormSubmittedModal({ formType, expirationDate, onClose }: Props) {
  const { getTranslation: t } = useTranslations();
  const isUnderReview = formType === TaxFormType.W8BENE;
  const replacements = {
    formType: getTaxFormLabel(formType),
    ...(expirationDate && { expirationDate: formatModalDate(expirationDate) }),
  };

  const titleKey = isUnderReview
    ? translationKeys.TAX_FORM_SUBMITTED_MODAL_REVIEW_TITLE
    : translationKeys.TAX_FORM_SUBMITTED_MODAL_ON_FILE_TITLE;
  const messageKey = isUnderReview
    ? translationKeys.TAX_FORM_SUBMITTED_MODAL_REVIEW_MESSAGE
    : expirationDate
      ? translationKeys.TAX_FORM_SUBMITTED_MODAL_ON_FILE_EXPIRING_MESSAGE
      : translationKeys.TAX_FORM_SUBMITTED_MODAL_ON_FILE_MESSAGE;

  const handleClose = () => {
    clearSubmittedTaxFormType();
    onClose();
  };

  return (
    <WhmDialog
      open
      onClose={handleClose}
      aria-labelledby="tax-form-submitted-dialog-title"
      fullWidth
      maxWidth="xs"
      data-test-id="tax-form-submitted-modal"
    >
      <WhmDialogTitle
        id="tax-form-submitted-dialog-title"
        variant="h4"
        sx={{ display: "flex", justifyContent: "space-between" }}
      >
        {t(titleKey)}
        <WhmIconButton
          aria-label="close"
          onClick={handleClose}
          size="small"
          sx={(theme: Theme) => ({
            padding: 0,
            paddingTop: "7px",
            alignSelf: "self-start",
            "&:hover": { backgroundColor: "#fff" },
            color: theme.palette.common.black,
          })}
        >
          <CloseIcon />
        </WhmIconButton>
      </WhmDialogTitle>
      <WhmDialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          src={taxFormSuccessImg}
          alt=""
          style={{ width: 124, height: 124, marginBottom: 24 }}
        />
        <WhmTypography variant="body1" sx={{ textAlign: "left" }}>
          <TranslateHtml
            translationKey={messageKey}
            parameters={replacements}
          />
        </WhmTypography>
      </WhmDialogContent>
      <WhmDialogActions sx={{ justifyContent: "flex-end" }}>
        <WhmButton
          variant="contained"
          color="primary"
          onClick={handleClose}
          data-test-id="tax-form-submitted-modal-action"
        >
          {t(translationKeys.TAX_FORM_SUBMITTED_MODAL_ACTION)}
        </WhmButton>
      </WhmDialogActions>
    </WhmDialog>
  );
}

export default TaxFormSubmittedModal;
